/*
 * ZK MOBILE DRAWER — behaviour for snippets/zk-mobile-drawer.liquid (the ZK-Theme mobile menu, ported).
 *
 * - Any element with aria-controls="<drawer id>" (the header's hamburger) opens/closes it; the hamburger shows an X
 *   while open (aria-expanded).
 * - The drawer is moved to <body> on load: the header section is sticky, and a positioned/transformed ancestor would
 *   otherwise trap the drawer's fixed position and stacking.
 * - It sits right under the header bar (ZK behaviour); the page behind it does not scroll while it is open.
 * - Closes on the X, Escape, the dimmed area (wider screens), any other click in the header (cart, search…), or a link.
 * - Accordions: [data-zkmd-toggle] buttons open the element named by their aria-controls.
 * - Theme editor: selecting one of its blocks opens the drawer on that block.
 */
(() => {
  if (window.customElements.get('zk-mobile-drawer')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  class ZkMobileDrawer extends HTMLElement {
    connectedCallback() {
      // Move to <body> first (this re-runs connectedCallback there). A previous copy, e.g. from before a theme editor
      // re-render of the header, is removed.
      if (this.parentElement !== document.body) {
        document.querySelectorAll(`zk-mobile-drawer[id="${this.id}"]`).forEach((other) => {
          if (other !== this) other.remove();
        });
        this.moving = true;
        document.body.appendChild(this);
        return;
      }
      this.moving = false;
      if (this.ready) return;
      this.ready = true;

      this.panel = this.querySelector('.zkmd__panel');
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      // On window (capture): runs before document-level capture listeners that stop the event (e.g. the ZK cart's
      // takeover listener for the cart icon), so the drawer always gets to close first.
      window.addEventListener('click', (event) => this.onDocumentClick(event), { capture: true, signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          // Let an open country/language popover (moved to <body> by the theme) close first.
          if (document.querySelector('x-popover[open]')) return;
          this.close();
        }
      }, { signal });
      // Any other theme dialog opening (cart, search, quick buy, popups…) closes the drawer. The drawer's own
      // country/language popover is the exception.
      document.addEventListener('toggle', (event) => {
        const dialog = event.target;
        if (!this.isOpen || !dialog || !dialog.hasAttribute || !dialog.hasAttribute('open')) return;
        if (this.contains(dialog) || (dialog.id || '').startsWith('popover-localization-zkmd-')) return;
        this.close({ restoreFocus: false });
      }, { signal });
      // Rotating / resizing into the desktop layout hides the hamburger: close the drawer there.
      const desktop = window.matchMedia('(min-width: 1000px)');
      const onViewportChange = () => {
        if (!this.isOpen) return;
        if (desktop.matches) this.close({ instant: true, restoreFocus: false });
        else this.place();
      };
      window.addEventListener('resize', onViewportChange, { signal });
      desktop.addEventListener('change', onViewportChange, { signal });
      window.addEventListener('pageshow', () => this.close({ instant: true }), { signal });

      // Theme editor: the drawer lives under <body>, so its blocks are matched by id (not by the event target).
      document.addEventListener('shopify:block:select', (event) => {
        const block = this.blockElement(event);
        if (!block) return;
        this.open({ instant: true, focus: false });
        block.scrollIntoView({ block: 'nearest' });
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (this.blockElement(event)) this.close({ instant: true, restoreFocus: false });
      }, { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.remove();
      }, { signal });

      this.syncToggles();
    }

    disconnectedCallback() {
      if (this.moving) return;
      this.abort?.abort();
      this.ready = false;
      document.documentElement.classList.remove('zkmd-lock');
    }

    blockElement(event) {
      if (this.contains(event.target)) return event.target;
      const detail = event.detail || {};
      if (!detail.blockId || (detail.sectionId && detail.sectionId !== this.dataset.sectionId)) return null;
      return [...this.querySelectorAll('[data-shopify-editor-block]')].find((el) => {
        try {
          return JSON.parse(el.getAttribute('data-shopify-editor-block')).id === detail.blockId;
        } catch (error) {
          return false;
        }
      }) || null;
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get toggles() {
      return [...document.querySelectorAll(`[aria-controls="${this.id}"]`)];
    }

    /**
     * The hamburger gets aria-expanded and, while open, an `open` attribute: the theme keeps a transparent header
     * solid and skips hide-on-scroll while an [open] element sits in the header navigation.
     */
    syncToggles() {
      this.toggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
        toggle.toggleAttribute('open', this.isOpen);
      });
    }

    /** Top edge = bottom of the header bar (as in ZK), so the hamburger/X stays visible and tappable. */
    place() {
      // x-header's own box includes the hide-on-scroll transform (the section's box does not).
      const header = document.querySelector('x-header') || document.querySelector('.shopify-section--header');
      const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      this.style.setProperty('--zkmd-top', `${bottom}px`);
      // Header scrolled out of view (non-sticky header): the drawer covers the X, so show its own close button.
      this.classList.toggle('is-detached', bottom === 0);
    }

    open({ instant = false, focus = true } = {}) {
      if (this.isOpen) return;
      this.opener = document.activeElement;
      this.place();
      if (instant) this.panel.style.transition = 'none';
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.documentElement.classList.add('zkmd-lock');
      this.syncToggles();
      if (instant) {
        this.panel.getBoundingClientRect();
        this.panel.style.transition = '';
      }
      if (focus) {
        setTimeout(() => this.panel.focus({ preventScroll: true }), reduceMotion.matches || instant ? 0 : 280);
      }
      this.dispatchEvent(new CustomEvent('zkmd:open', { bubbles: true }));
    }

    close({ instant = false, restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      if (instant) this.panel.style.transition = 'none';
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      document.documentElement.classList.remove('zkmd-lock');
      this.syncToggles();
      if (instant) {
        this.panel.getBoundingClientRect();
        this.panel.style.transition = '';
      }
      if (restoreFocus && this.opener && this.opener.isConnected && typeof this.opener.focus === 'function') {
        this.opener.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('zkmd:close', { bubbles: true }));
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    onDocumentClick(event) {
      const toggle = event.target.closest?.(`[aria-controls="${this.id}"]`);
      if (toggle) {
        event.preventDefault();
        this.toggle();
        return;
      }
      // Another header action (cart, search, account, logo) closes the drawer and carries on as normal.
      if (this.isOpen && !this.contains(event.target) && event.target.closest?.('.shopify-section--header, x-header')) {
        this.close({ restoreFocus: false });
      }
    }

    onClick(event) {
      if (event.target.closest('[data-zkmd-close]')) {
        this.close();
        return;
      }

      const toggle = event.target.closest('[data-zkmd-toggle]');
      if (toggle) {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        const target = document.getElementById(toggle.getAttribute('aria-controls'));
        if (target) target.hidden = expanded;
        const item = toggle.closest('[data-open]');
        if (item) item.dataset.open = expanded ? 'false' : 'true';
        return;
      }

      // Following a link closes the drawer (matters for same-page links and for the back/forward cache).
      const link = event.target.closest('a[href]');
      if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey) this.close({ restoreFocus: false });
    }

    /** Keeps Tab inside the open drawer. */
    onKeydown(event) {
      if (event.key !== 'Tab' || !this.isOpen) return;
      const focusables = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  window.customElements.define('zk-mobile-drawer', ZkMobileDrawer);
})();
